import type {
  ProjectFormData,
  ProjectMappingType,
} from '../../../interfaces/ui/addProjectModal';
import type { Option } from '../../../interfaces/ui/addProjectModal';
import type {
  JiraTestPlan,
  JiraTestSuite,
  TestPlan,
  TestSuite,
} from '../../../interfaces/ui/testPlan';

export const getTestSuiteOptions = (
  projectMappingType: ProjectMappingType,
  selectedTestPlanId: string,
  testPlans: TestPlan[],
  testSuites: TestSuite[],
): Option[] => {
  if (!selectedTestPlanId) return [];

  if (projectMappingType === 'JIRA') {
    const selectedTestPlan = testPlans.find(
      (testPlan: TestPlan) => testPlan.id.toString() === selectedTestPlanId,
    ) as JiraTestPlan;

    return (
      selectedTestPlan?.children?.map((suite: JiraTestSuite) => ({
        label: suite.name,
        value: suite.id.toString(),
      })) || []
    );
  }

  if (projectMappingType === 'ADO') {
    return testSuites.map((suite: TestSuite) => ({
      label: suite.name,
      value: suite.id.toString(),
    }));
  }

  return [];
};

//  ──────────────────────────────────────────────────────
// Form Field Placeholder Text helper functions
//  ──────────────────────────────────────────────────────

export function getMappedProjectPlaceHolderText(
  mappingType: ProjectMappingType,
  loading: boolean,
  mappedProjectsCount: number,
): string {
  if (!mappingType) return 'Select ADO or JIRA first';
  if (loading) return 'Loading mapped projects...';
  if (mappedProjectsCount === 0) return 'No mapped projects found';
  return 'Select a mapped project';
}

export const getTestPlanPlaceHolderText = (
  mappedProjectId: string,
  loading: boolean,
  testPlansCount: number,
  isJira: boolean,
): string => {
  if (!mappedProjectId) return 'Select a mapped project first';
  if (loading) return 'Loading test plans...';
  if (testPlansCount === 0)
    return isJira ? 'No folders found' : 'No test plans found';
  return isJira ? 'Select a folder' : 'Select a test plan';
};

export const getTestSuitePlaceHolderText = (
  testPlanId: string,
  loading: boolean,
  testSuitesCount: number,
  isJira: boolean,
): string => {
  if (!testPlanId)
    return isJira ? 'Select a folder first' : 'Select a test plan first';
  if (loading) return 'Loading test suites...';
  if (testSuitesCount === 0)
    return isJira ? 'No sub-folders found' : 'No test suites found';
  return isJira ? 'Select a sub-folder' : 'Select a test suite';
};

export const getFlowPlaceHolderText = (flowsCount: number): string => {
  if (flowsCount === 0) return 'No flows found';
  return 'Select a flow';
};

//  ──────────────────────────────────────────────────────
// Form Dependency values Reset
//  ──────────────────────────────────────────────────────
type ResetFormField =
  | 'projectMappingType'
  | 'mappedProjectName'
  | 'testPlanName'
  | 'testSuiteName'
  | 'flowName'
  | 'existingChecked'
  | 'existingTestPlan';

// Reset dependent form data fields based on the field that changed.

export function getResetDependentFormFields(
  field: ResetFormField,
): Partial<ProjectFormData> {
  switch (field) {
    case 'projectMappingType':
      return {
        ...resetMappedProject,
        ...resetTestPlan,
        ...resetTestSuite,
        ...resetExistingTestPlan,
        ...resetExistingTestSuite,
      };

    case 'mappedProjectName':
      return {
        ...resetTestPlan,
        ...resetTestSuite,
        ...resetExistingTestPlan,
        ...resetExistingTestSuite,
      };
    case 'testPlanName':
      return {
        ...resetTestSuite,
      };
    case 'existingChecked':
      return {
        ...resetExistingTestPlan,
        ...resetExistingTestSuite,
      };
    case 'existingTestPlan':
      return {
        ...resetExistingTestSuite,
      };
    default:
      return {};
  }
}

const resetMappedProject = {
  mappedProjectId: '',
  mappedProjectName: '',
  mappedProjectOrg: '',
};

const resetTestPlan = {
  testPlanId: '',
  testPlanName: '',
};

const resetTestSuite = {
  testSuiteId: '',
  testSuiteName: '',
};

const resetExistingTestPlan = {
  existingTestPlanId: '',
  existingTestPlanName: '',
};

const resetExistingTestSuite = {
  existingTestSuiteId: '',
  existingTestSuiteName: '',
};
